import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '~/lib/supabase-browser';
import { useAuth } from '~/contexts/AuthContext';

// MFA gateway. Used for two states:
//   - User signed in at AAL1 with no verified factor → enroll a TOTP factor
//     (show QR + secret, prompt for first code, challengeAndVerify).
//   - User signed in at AAL1 with a verified factor → challenge for current code.
// Successful verify upgrades the session to AAL2, after which the rest of
// the app (and the edge gate) lets the user through.

type Mode = 'loading' | 'enroll' | 'challenge';

interface EnrollFactor {
  id: string;
  totp: { qr_code: string; secret: string; uri: string };
}

export default function MFA() {
  const { user, loading, currentAAL, refreshAAL, signOut } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/dashboard';

  const [mode, setMode] = useState<Mode>('loading');
  const [enrollment, setEnrollment] = useState<EnrollFactor | null>(null);
  const [challengeFactorId, setChallengeFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the user lands on /mfa already at AAL2, they're done — bounce them.
  useEffect(() => {
    if (!loading && user && currentAAL === 'aal2') {
      navigate(next, { replace: true });
    }
  }, [loading, user, currentAAL, navigate, next]);

  // If the user is not signed in at all, send them to /login.
  useEffect(() => {
    if (!loading && !user) {
      navigate(`/login?next=${encodeURIComponent('/mfa')}`, { replace: true });
    }
  }, [loading, user, navigate]);

  // Initialize: list factors, choose enroll vs challenge.
  useEffect(() => {
    if (loading || !user || currentAAL === 'aal2') return;
    let cancelled = false;
    (async () => {
      setError(null);
      const { data, error: listErr } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      if (listErr) {
        setError(listErr.message);
        setMode('enroll');
        return;
      }
      const verifiedTotp = (data?.totp ?? []).find((f) => f.status === 'verified');
      if (verifiedTotp) {
        setChallengeFactorId(verifiedTotp.id);
        setMode('challenge');
      } else {
        const { data: enroll, error: enrollErr } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `BidetNY admin (${new Date().toISOString().slice(0, 10)})`,
        });
        if (cancelled) return;
        if (enrollErr || !enroll) {
          setError(enrollErr?.message ?? 'Could not start enrollment');
          setMode('enroll');
          return;
        }
        setEnrollment(enroll as EnrollFactor);
        setMode('enroll');
      }
    })();
    return () => { cancelled = true; };
  }, [loading, user, currentAAL]);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      const factorId = mode === 'enroll' ? enrollment?.id : challengeFactorId;
      if (!factorId) throw new Error('No factor available');
      const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
      if (verifyErr) {
        setError(verifyErr.message);
        setBusy(false);
        return;
      }
      await refreshAAL();
      navigate(next, { replace: true });
    } catch (err: any) {
      setError(err?.message ?? String(err));
      setBusy(false);
    }
  }

  if (loading || mode === 'loading') {
    return <main className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading…</div></main>;
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-6 space-y-4">
        <h1 className="text-xl font-semibold">
          {mode === 'enroll' ? 'Set up two-factor auth' : 'Two-factor auth'}
        </h1>
        {mode === 'enroll' && enrollment && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Scan this QR code with an authenticator app (1Password, Authy, Google Authenticator, etc.), then enter the 6-digit code it shows.
            </p>
            <div className="flex justify-center">
              <img src={enrollment.totp.qr_code} alt="TOTP enrollment QR" className="w-48 h-48 border border-gray-200 rounded" />
            </div>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">Can't scan? Enter manually</summary>
              <p className="mt-1 break-all font-mono">{enrollment.totp.secret}</p>
            </details>
          </div>
        )}
        {mode === 'challenge' && (
          <p className="text-sm text-gray-700">
            Enter the current 6-digit code from your authenticator app.
          </p>
        )}
        <form onSubmit={handleVerify} className="space-y-3">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            required
            minLength={6}
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            className="w-full text-center tracking-widest text-2xl rounded border border-gray-300 px-3 py-2"
            autoFocus
          />
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="w-full rounded bg-black text-white py-2 disabled:opacity-50"
          >
            {busy ? 'Verifying…' : 'Verify'}
          </button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </form>
        <button
          onClick={() => signOut().then(() => navigate('/login', { replace: true }))}
          className="text-xs text-gray-500 underline"
        >
          Sign out and try a different account
        </button>
      </div>
    </main>
  );
}
